import * as v from 'valibot';
import { Envelope } from '../shared/types/common.model';
import { INVALID_RESPONSE_FORMAT_ERROR, UNEXPECTED_ERROR } from './constants';

export class ClientRequestHandler {
  /**
   * The source identifier for the client making the request.
   * Used for error reporting and tracing the origin of client errors.
   */
  source: string;

  constructor(source: string) {
    this.source = source;
  }

  /**
   * Handles a request and validates the response data against the provided Valibot object schema.
   * @param request - A Promise of an Envelope-wrapped response
   * @param schema - A Valibot object schema (e.g., from v.object\(\{ ... \}\)) to validate the response data
   * @returns The validated response data
   * @throws ClientError if validation fails or an unexpected error occurs
   */
  async handleRequest<T>(
    request: Promise<Envelope<T>>,
    schema: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>
  ): Promise<T> {
    try {
      const response = await request;
      v.parse(schema, response.data);
      return response.data;
    } catch (e) {
      if (e instanceof v.ValiError) {
        throw new ClientError(this.source, INVALID_RESPONSE_FORMAT_ERROR, e.issues);
      }
      throw new ClientError(this.source, UNEXPECTED_ERROR);
    }
  }
}

/**
 * Represents an error thrown by the client request handler.
 * Includes the source of the error, a message, and optional details for debugging.
 */
class ClientError extends Error {
  constructor(
    source: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = source;
    this.details = details;
  }
}
